import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../../context/AuthContext";
import { useUIContext } from "../../../../context/UIContext";
import useSurveys from "../../../../hooks/useSurveys";
import useSurveyResponses from "../../../../hooks/useSurveyResponses";
import useSurveyQuestions from "../../../../hooks/useSurveyQuestions";
import useLessonStudents from "../../../../hooks/useLessonStudentData";
import useOrganizationSetting from "../../../../hooks/useOrganizationSetting";
import useTermPeriod from "../../../../hooks/useTermPeriod";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import { setTermIdByToday } from "../../../../util/dateRelated";
import DropdownSchoolYear from "../../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownTermDate from "../../../../unicomponents/Dropdowns/DropdownTermDate";
import DropdownUniqueItems from "../../../../unicomponents/Dropdowns/DropdownUniqueItems";
import HelpHyperLink from "../../../../unicomponents/HelpHyperLink";
import DynamicTable from "../../../../unicomponents/Tables/DynamicTable";

export default function SubjectSurveyStatistics({ schoolId }) {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuthContext();
  const { setIsLoadingModal } = useUIContext();

  // 기본 조건 설정
  const conditions = { schoolid: schoolId };

  // 상태 관리
  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    selectedSurveyId: "",
    selectedSubjectId: "",
    termName: "",
  });

  const [termId, setTermId] = useState("");
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [processedTableData, setProcessedTableData] = useState([]);
  const [lessonList, setLessonList] = useState([]);

  // Hook 사용
  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting(conditions);

  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod(conditions);

  // 수업설문 조회 조건
  const surveyConditions = {
    schoolid: schoolId,
    survey_type: "bySubject",
    response_target: "student",
  };

  const {
    surveysQuery: { data: surveysData },
  } = useSurveys(surveyConditions);

  // FlexibleData 조회
  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "surveyStatistics",
  });

  // 과목 데이터 조회를 위한 훅
  const { getAllLessonStudentsBySchoolYear } = useLessonStudents();
  const getAllLessonStudentsBySchoolYearRef = useRef(
    getAllLessonStudentsBySchoolYear
  );

  // 설문 응답 조회 조건
  const responseConditions = {
    schoolid: schoolId,
    survey_id: filters.selectedSurveyId,
    // ...(filters.selectedSubjectId && { subject_id: filters.selectedSubjectId }),
  };

  const {
    surveyResponsesQuery: { data: responsesData },
  } = useSurveyResponses(filters.selectedSurveyId ? responseConditions : {});

  // 설문 질문 조회 조건
  const questionsConditions = {
    schoolid: schoolId,
    survey_id: filters.selectedSurveyId,
  };

  const {
    surveyQuestionsAllQuery: { data: questionsData },
  } = useSurveyQuestions(filters.selectedSurveyId ? questionsConditions : {});

  // 초기 학년도 설정
  useEffect(() => {
    if (filters.schoolYear === null && settingData && settingData.length > 0) {
      const initialData = settingData.sort(
        (a, b) => b.school_year - a.school_year
      )[0];

      const initialSchoolYear = initialData.school_year;
      const initialSchoolYearId = initialData.id;

      setFilters((prev) => ({
        ...prev,
        schoolYear: initialSchoolYear,
        schoolYearId: initialSchoolYearId,
      }));

      if (termPeriodData) {
        setTermIdByToday(termPeriodData, setTermId, initialSchoolYearId);
      }
    }
  }, [filters.schoolYear, settingData, termPeriodData]);

  // termId가 변경될 때마다 termName 설정
  useEffect(() => {
    if (termId && termPeriodData) {
      const selectedTerm = termPeriodData.find(
        (term) => term.id.toString() === termId.toString()
      );

      setFilters((prev) => ({
        ...prev,
        termName: selectedTerm?.term_name || "",
      }));
    } else {
      // termId가 없거나 유효하지 않을 때 termName 초기화
      setFilters((prev) => ({
        ...prev,
        termName: "",
      }));
    }
  }, [termId, termPeriodData]);

  // 과목 데이터 조회
  useEffect(() => {
    if (filters.schoolYearId && filters.termName) {
      const forWhere = {
        schoolid: schoolId,
        school_year_id: filters.schoolYearId,
      };

      setIsLoadingModal(true);
      getAllLessonStudentsBySchoolYearRef.current.mutate(forWhere, {
        onSuccess: (data) => {
          setIsLoadingModal(false);

          const sortedLessons = data.filter(
            (r) =>
              r.school_year_id.toString() === filters.schoolYearId.toString() &&
              r.lesson?.TermDates?.term_name === filters.termName
          );

          let subjects = sortedLessons.map((r) => ({
            ...r.lesson,
            lesson_id: r.lesson_id, // lesson_id 속성 추가
          }));
          //   console.log("subjects", subjects);
          // 권한별 필터링
          if (!hasPermission(null, "can_read")) {
            subjects = subjects.filter((subject) =>
              subject.teacherUsernames?.includes(user.username)
            );
          }

          setAvailableSubjects(sortedLessons);
          setLessonList(subjects);
        },
        onError: (error) => {
          setIsLoadingModal(false);
          console.error("과목 조회 오류:", error);
        },
      });
    } else {
      setAvailableSubjects([]);
      setLessonList([]);
    }
  }, [
    filters.schoolYearId,
    termId,
    schoolId,
    user,
    hasPermission,
    setIsLoadingModal,
    filters.termName,
  ]);

  // 설문 응답 데이터 처리
  useEffect(() => {
    // 학기가 선택되지 않았으면 빈 배열로 설정
    if (!filters.termName) {
      setProcessedTableData([]);
      return;
    }

    if (responsesData && questionsData && settingData) {
      // 학년도와 학기로 필터링
      const filteredResponses = responsesData.filter((response) => {
        // 학년도 필터링: 선택된 학년도 ID와 response의 lesson.school_year_id 비교
        const schoolYearMatch = filters.schoolYearId
          ? response.lesson?.school_year_id?.toString() ===
            filters.schoolYearId?.toString()
          : true;

        // 학기 필터링: 선택된 학기명과 response의 school_term 비교
        const termMatch = filters.termName
          ? response.school_term?.toString() === filters.termName?.toString()
          : false;

        // 과목 필터링: 선택된 과목 ID와 response의 subject_id 비교
        const subjectMatch = filters.selectedSubjectId
          ? response.subject_id?.toString() ===
            filters.selectedSubjectId?.toString()
          : true;

        // 제출 완료 필터링: is_submitted가 true인 것만
        const submittedMatch = response.is_submitted === true;

        return schoolYearMatch && termMatch && subjectMatch && submittedMatch;
      });

      const processedData = filteredResponses.map((response) => {
        // 학생 정보 매칭
        const student = response.user || {};

        // 설문 답변을 질문별로 매핑
        const answerMap = {};
        if (response.answers) {
          response.answers.forEach((answer) => {
            const question = questionsData.find(
              (q) => q.id === answer.question_id
            );
            if (question) {
              let displayValue = "";

              if (
                question.question_type === "multiple_choice" &&
                answer.selected_option_id
              ) {
                const option = question.options?.find(
                  (opt) => opt.id === answer.selected_option_id
                );
                displayValue = option?.option_text || answer.answer_text || "";
              } else if (
                question.question_type === "checkbox" &&
                answer.selected_checkbox_ids
              ) {
                const selectedOptions = question.options?.filter((opt) =>
                  answer.selected_checkbox_ids.includes(opt.id)
                );
                displayValue =
                  selectedOptions?.map((opt) => opt.option_text).join(", ") ||
                  "";
              } else {
                displayValue = answer.answer_text || "";
              }

              answerMap[`response_${question.id}`] = displayValue;
            }
          });
        }

        // organization_path에서 과정/학년/반 정보 추출
        // 해당 학년도에 맞는 registration 정보를 찾아서 사용
        let hierarchyOne = "-",
          hierarchyTwo = "-",
          hierarchyThree = "-";

        // availableSubjects에서 해당 과목의 hierarchy 정보 찾기
        const subjectInfo = availableSubjects.find(
          (s) => s.lesson_id === response.subject_id
        );

        if (response.registration?.organization_path && settingData) {
          // fallback: response의 registration 정보 사용
          const pathParts = response.registration.organization_path.split("/");
          const settingMap = settingData.reduce((map, setting) => {
            map[setting.id] = setting.group_name;
            return map;
          }, {});

          hierarchyOne = settingMap[pathParts[2]] || "-"; // 과정
          hierarchyTwo = settingMap[pathParts[3]] || "-"; // 학년
          hierarchyThree = settingMap[pathParts[4]] || "-"; // 반
        }

        // 교사 정보 처리 - availableSubjects에서 우선 가져오고, 없으면 response에서
        const teachers =
          subjectInfo?.teachers ||
          response.lesson?.teachers
            ?.map((teacher) => teacher.tname)
            .join(", ") ||
          "-";

        return {
          id: response.id,
          subject_id: response.subject_id, // 권한 필터링을 위해 추가
          submitted_at: response.submitted_at
            ? new Date(response.submitted_at).toLocaleDateString("ko-KR")
            : "-",
          schoolYear: response.school_year || filters.schoolYear,
          termName: response.school_term || filters.termName,
          hierarchyOne: hierarchyOne,
          hierarchyTwo: hierarchyTwo,
          hierarchyThree: hierarchyThree,
          fullname: student.fullname || "",
          otherName: student.other_name || "", // 다른 이름 추가
          username: response.username || "", // 익명 설문의 경우 빈 문자열일 수 있음
          subjectName:
            subjectInfo?.subject_name ||
            response.lesson?.subject_name ||
            response.subjectName ||
            "-",
          teachers: teachers,
          ...answerMap, // 동적 응답 컬럼들
        };
      });

      // 권한별 필터링
      let filteredData = processedData;
      if (user?.role === "teacher" && !hasPermission(null, "can_read")) {
        // 교사는 본인이 담당하는 과목의 응답만 볼 수 있음
        filteredData = processedData.filter((item) => {
          // filteredResponses에서 해당 응답 찾기
          const responseData = filteredResponses.find((r) => r.id === item.id);
          const teacherUsernames =
            responseData?.lesson?.teachers?.map((t) => t.username) || [];
          return teacherUsernames.includes(user.username);
        });
      }

      setProcessedTableData(filteredData);
    } else {
      setProcessedTableData([]);
    }
  }, [
    responsesData,
    questionsData,
    settingData,
    availableSubjects,
    filters,
    user,
    hasPermission,
  ]);

  // 드롭다운 변경 핸들러
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0]?.text || "";

    if (name === "survey") {
      setFilters((prev) => ({
        ...prev,
        selectedSurveyId: value,
        selectedSubjectId: "", // 설문 변경시 과목 선택 초기화
      }));

      const selectedSurvey = surveysData?.find(
        (s) => s.id.toString() === value
      );
      setSelectedSurvey(selectedSurvey);
    }

    if (name === "0") {
      setFilters((prev) => ({
        ...prev,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        selectedSubjectId: "", // 학년도 변경시 과목 선택 초기화
      }));

      setTermIdByToday(termPeriodData, setTermId, value);
    }

    if (name === "termDate") {
      console.log("value", value);
      setTermId(value);
      setFilters((prev) => ({
        ...prev,
        selectedSubjectId: "", // 학기 변경시 과목 선택 초기화
        termName: !value || value < 1 ? "" : prev.termName, // value가 null, undefined, < 1이면 termName 초기화
      }));
    }

    if (name === "subject") {
      setFilters((prev) => ({
        ...prev,
        selectedSubjectId: value,
      }));
    }
  };

  // 테이블 컬럼 구성
  const generateTableConfig = () => {
    if (!questionsData || questionsData.length === 0) {
      return null;
    }

    // 질문 필터링: is_common과 subject_id 기준
    const filteredQuestions = questionsData.filter((question) => {
      if (filters.selectedSubjectId) {
        // 과목이 선택된 경우: is_common이 true이거나, subject_id가 선택된 과목과 일치하는 질문
        return (
          question.is_common === true ||
          (question.is_common === false &&
            question.subject_id?.toString() ===
              filters.selectedSubjectId?.toString())
        );
      } else {
        // 과목이 선택되지 않은 경우: is_common이 true인 질문만
        return question.is_common === true;
      }
    });

    // 기본 헤더들
    const defaultHeaders = [
      {
        headerKey: "submitted_at",
        label: t("teacher.surveyStatistics.submittedAt") || "제출일",
        style: { minWidth: "80px" },
        sortField: "submitted_at",
      },
      {
        headerKey: "schoolYear",
        label: t("common.labels.schoolYear") || "학년도",
        style: { minWidth: "50px" },
        sortField: "schoolYear",
      },
      {
        headerKey: "termName",
        label: t("common.labels.termName") || "학기",
        style: { minWidth: "50px" },
        sortField: "termName",
      },
    ];

    // 익명 설문이 아닌 경우에만 개인정보 관련 컬럼 추가
    if (!selectedSurvey?.is_anonymous) {
      defaultHeaders.push(
        {
          headerKey: "hierarchyOne",
          label: t("common.labels.schoolStage") || "과정",
          style: { minWidth: "50px" },
          sortField: "hierarchyOne",
        },
        {
          headerKey: "hierarchyTwo",
          label: t("common.labels.studentYear") || "학년",
          style: { minWidth: "50px" },
          sortField: "hierarchyTwo",
        },
        {
          headerKey: "hierarchyThree",
          label: t("common.labels.class") || "반",
          style: { minWidth: "50px" },
          sortField: "hierarchyThree",
        },
        {
          headerKey: "fullname",
          label: t("common.user.fullname") || "이름",
          style: { minWidth: "50px" },
          sortField: "fullname",
        }
      );
    }

    // FlexibleData 기반 옵션 컬럼들
    const optionalHeaders = [];

    // 다른 이름 컬럼 (익명 설문이 아닌 경우에만)
    if (
      !selectedSurvey?.is_anonymous &&
      flexibleData &&
      flexibleData[0]?.data_value?.columns?.includes("otherName")
    ) {
      optionalHeaders.push({
        headerKey: "otherName",
        label: t("common.user.otherName") || "다른 이름",
        style: { minWidth: "50px" },
        sortField: "otherName",
      });
    }

    // 아이디 컬럼 (기본으로 숨김, flexibleData로 표시할 수 있음)
    if (
      flexibleData &&
      flexibleData[0]?.data_value?.columns?.includes("username")
    ) {
      optionalHeaders.push({
        headerKey: "username",
        label: t("common.user.username") || "사용자계정",
        style: { minWidth: "50px" },
        sortField: "username",
      });
    }

    // 과목 정보
    optionalHeaders.push({
      headerKey: "subjectName",
      label: t("teacher.surveyStatistics.subject") || "과목",
      style: { minWidth: "50px" },
      sortField: "subjectName",
    });

    // 과목교사 컬럼 (기본으로 표시, flexibleData로 숨길 수 있음)
    if (
      !flexibleData ||
      !flexibleData[0]?.data_value?.hiddenColumns?.includes("teachers")
    ) {
      optionalHeaders.push({
        headerKey: "teachers",
        label: t("teacher.surveyStatistics.teachers") || "과목교사",
        style: { minWidth: "50px" },
        sortField: "teachers",
      });
    }

    // 동적 응답 컬럼들 (필터링된 질문들로 생성)
    const responseHeaders = filteredQuestions.map((question, index) => ({
      headerKey: `response_${question.id}`,
      label: `${question.question_text}`,
      style: {
        minWidth: "100px",
        maxWidth: "200px", // 설문 질문은 좀 더 넓게
        wordWrap: "break-word",
        whiteSpace: "normal",
      },
      sortField: `response_${question.id}`,
    }));

    const headers = [...defaultHeaders, ...optionalHeaders, ...responseHeaders];

    // 컬럼 설정
    const columns = headers.map((header) => ({
      valueKey: header.headerKey,
      cellStyle: {
        maxWidth: header.style.maxWidth,
        wordWrap: "break-word",
        whiteSpace: "normal",
        verticalAlign: "top", // 줄바꿈된 텍스트의 정렬을 위해
      },
    }));

    return {
      style: { fontSize: "12px", tableLayout: "auto" },
      headers,
      columns,
      rows: processedTableData,
    };
  };

  const tableConfig = generateTableConfig();

  // 미응답자 수 계산
  const getUnsubmittedCount = () => {
    if (
      !filters.selectedSubjectId ||
      !availableSubjects ||
      !processedTableData ||
      selectedSurvey?.is_anonymous // 익명 설문인 경우 미응답자 수 계산하지 않음
    ) {
      return 0;
    }

    try {
      // 선택된 과목의 모든 수강생
      const selectedSubjectStudents = availableSubjects.filter(
        (item) =>
          item.lesson_id?.toString() === filters.selectedSubjectId?.toString()
      );

      // 응답한 학생들의 username 목록 (processedTableData에서)
      // 익명 설문의 경우 username이 빈 문자열일 수 있으므로 filter(Boolean)로 빈 값 제외
      const respondedStudentUsernames = new Set(
        processedTableData.map((data) => data.username).filter(Boolean)
      );

      // 미응답 학생 수 계산
      const unsubmittedCount = selectedSubjectStudents.filter((studentData) => {
        const studentUsername =
          studentData.student?.username || studentData.username;
        return (
          studentUsername && !respondedStudentUsernames.has(studentUsername)
        );
      }).length;

      return unsubmittedCount;
    } catch (error) {
      console.warn("미응답자 수 계산 중 오류:", error);
      return 0;
    }
  };

  // 미제출자 명단 계산 (기존 함수는 유지)
  const getUnsubmittedStudents = () => {
    if (
      !filters.selectedSubjectId ||
      !availableSubjects ||
      !processedTableData ||
      selectedSurvey?.is_anonymous // 익명 설문인 경우 미응답자 명단 계산하지 않음
    ) {
      return [];
    }

    try {
      // 선택된 과목의 모든 수강생
      const selectedSubjectStudents = availableSubjects.filter(
        (item) =>
          item.lesson_id?.toString() === filters.selectedSubjectId?.toString()
      );

      // 응답한 학생들의 username 목록 (processedTableData에서)
      // 익명 설문의 경우 username이 빈 문자열일 수 있으므로 filter(Boolean)로 빈 값 제외
      const respondedStudentUsernames = new Set(
        processedTableData.map((data) => data.username).filter(Boolean)
      );

      // 미응답 학생들 찾기
      const unsubmittedStudents = selectedSubjectStudents
        .filter((studentData) => {
          const studentUsername =
            studentData.student?.username || studentData.username;
          return (
            studentUsername && !respondedStudentUsernames.has(studentUsername)
          );
        })
        .map((studentData) => ({
          id: studentData.student_id || studentData.user_id || studentData.id,
          fullname:
            studentData.student?.fullname ||
            studentData.fullname ||
            "이름 없음",
          hierarchyTwo: studentData.hierarchyTwo || "-",
          hierarchyThree: studentData.hierarchyThree || "-",
          username: studentData.student?.username || studentData.username || "",
        }))
        .sort((a, b) => a.fullname.localeCompare(b.fullname));

      return unsubmittedStudents;
    } catch (error) {
      console.warn("미응답자 명단 계산 중 오류:", error);
      return [];
    }
  };

  const unsubmittedStudents = getUnsubmittedStudents();
  const unsubmittedCount = getUnsubmittedCount();

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap mb-3 mt-2 gap-3">
        <div className="col-auto me-3">
          <h3 className="fw-bold">
            {t("teacher.surveyStatistics.subjectSurveyTitle") ||
              "수업설문 통계"}
            <HelpHyperLink link={""} />
          </h3>
        </div>
        {/* {console.log("responsesData", responsesData)} */}
        {/* 설문 선택 */}
        <div style={{ minWidth: "150px" }} className="me-1">
          <label className="form-label small">
            {t("teacher.surveyStatistics.selectSurvey") || "설문지 선택"}
          </label>
          <select
            name="survey"
            className="form-control"
            value={filters.selectedSurveyId}
            onChange={handleDropdownChange}
          >
            <option value="">
              {surveysData?.filter(
                (survey) =>
                  survey.status === "respondable" ||
                  survey.status === "readable"
              ).length > 0
                ? t("teacher.surveyStatistics.selectOption") || "선택하세요"
                : t("teacher.surveyStatistics.noSurveyAvailable") ||
                  "설문이 없습니다"}
            </option>
            {surveysData
              ?.filter(
                (survey) =>
                  survey.status === "respondable" ||
                  survey.status === "readable"
              )
              .map((survey) => (
                <option key={survey.id} value={survey.id}>
                  {survey.title}
                </option>
              ))}
          </select>
        </div>

        {/* 학년도 선택 */}
        <div style={{ width: "95px" }} className="me-1">
          <label className="form-label small">
            {t("common.labels.schoolYear") || "학년도"}
          </label>
          <DropdownSchoolYear
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYearId={filters.schoolYearId}
            allYears={false}
          />
        </div>

        {/* 학기 선택 */}
        <div style={{ width: "95px" }} className="me-1">
          <label className="form-label small">
            {t("common.labels.termName") || "학기"}
          </label>
          <DropdownTermDate
            termPeriodData={termPeriodData}
            handleDropdownChange={handleDropdownChange}
            termId={termId}
            schoolYearId={filters.schoolYearId}
          />
        </div>

        {/* 과목 선택 */}
        <div style={{ minWidth: "150px" }} className="me-1">
          <label className="form-label small">
            {t("teacher.surveyStatistics.subject") || "과목"}
          </label>
          <DropdownUniqueItems
            displayData={lessonList}
            displayKey={"subject_name"}
            valueKey={"lesson_id"}
            uniqueKey={"lesson_id"}
            sortKey={"subject_name"}
            isDesc={false}
            handleDropdownChange={handleDropdownChange}
            selectedItem={filters.selectedSubjectId}
            selectName="subject"
            dropdownTitle={
              t("teacher.surveyStatistics.allSubjects") || "전체 과목"
            }
            titleValue=""
          />
        </div>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div className="row">
        {/* <div className="col-12"> */}
        {filters.selectedSurveyId ? (
          <div>
            {/* <div className="card-body"> */}
            <h4 className="card-title">{selectedSurvey?.title}</h4>
            {selectedSurvey?.description && (
              <p className="card-text mt-2">{selectedSurvey.description}</p>
            )}

            {/* 선택된 조건 표시 */}
            <div className="alert alert-info p-1" style={{ fontSize: "13px" }}>
              <div className="row">
                <div className="col-md-2">
                  <strong>
                    {t("teacher.surveyStatistics.responseCount") || "응답 수"}:
                  </strong>{" "}
                  {processedTableData.length}건
                  <br />
                  {filters.selectedSubjectId && (
                    <>
                      <strong>미응답자:</strong> {unsubmittedCount}명
                    </>
                  )}
                </div>
                <div className="col-md-10">
                  {selectedSurvey?.is_anonymous ? (
                    <span className="text-muted">
                      (익명 설문으로 인해 미응답자 명단은 표시되지 않습니다)
                    </span>
                  ) : (
                    unsubmittedStudents.map((student, index) => (
                      <span key={student.id}>
                        {student.fullname}
                        {index < unsubmittedStudents.length - 1 ? ", " : ""}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* DynamicTable */}
            {tableConfig && processedTableData.length > 0 ? (
              <div
                style={{
                  overflowX: "auto",
                }}
              >
                <DynamicTable
                  tableConfig={tableConfig}
                  dataStructure="flat"
                  tableStyle="spreadSheet"
                  pagination={true}
                  pageSize={50}
                  initialPage={1}
                  sortable={true}
                  initialSortField="submitted_at"
                  initialSortDirection="desc"
                  searchable={true}
                  searchPlaceholder={
                    t("teacher.surveyStatistics.searchPlaceholder") ||
                    "이름, 과목, 교사 검색..."
                  }
                  searchFields={
                    selectedSurvey?.is_anonymous
                      ? ["subjectName", "teachers"]
                      : ["fullname", "otherName", "subjectName", "teachers"]
                  }
                  exportable={true}
                  exportFileName={`${selectedSurvey?.title || ""}-${
                    filters.schoolYear || ""
                  }-${filters.termName || ""}-${
                    filters.selectedSubjectId
                      ? lessonList.find(
                          (s) =>
                            s.lesson_id?.toString() ===
                            filters.selectedSubjectId?.toString()
                        )?.subject_name || "과목"
                      : "전체과목"
                  }-설문응답통계`}
                  exportButtonLabel="Excel"
                  filterable={true}
                  filterableColumns={
                    selectedSurvey?.is_anonymous
                      ? ["subjectName", "teachers"]
                      : [
                          "hierarchyOne",
                          "hierarchyTwo",
                          "hierarchyThree",
                          "fullname",
                          "otherName",
                          "subjectName",
                          "teachers",
                        ]
                  }
                  rowNumber={true}
                />
              </div>
            ) : filters.selectedSurveyId && questionsData ? (
              <div className="text-center mt-4">
                <p className="text-muted">
                  {t("teacher.surveyStatistics.noResponseData") ||
                    "응답 데이터가 없습니다."}
                </p>
              </div>
            ) : (
              <div className="text-center mt-4">
                <p className="text-muted">
                  {t("teacher.surveyStatistics.loadingData") ||
                    "데이터를 불러오는 중..."}
                </p>
              </div>
            )}
            {/* </div> */}
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center">
              <h5 className="card-title text-muted">
                {t("teacher.surveyStatistics.pleaseSelectSurvey") ||
                  "설문을 선택해주세요"}
              </h5>
              <p className="card-text text-muted">
                {t("teacher.surveyStatistics.selectSurveyInstruction") ||
                  "설문지를 선택하시면 해당 설문의 응답 통계를 확인할 수 있습니다."}
              </p>
            </div>
          </div>
        )}
        {/* </div> */}
      </div>
    </div>
  );
}
